// src/components/tour-packages/forms/CreateTourPackageForm.tsx

import React, { useEffect, useState, type BaseSyntheticEvent } from "react";
import {
  Controller,
  type UseFormReturn,
  type UseFieldArrayReturn,
} from "react-hook-form";
import {
  FiInfo,
  FiMapPin,
  FiList,
  FiImage,
  FiDollarSign,
  FiX,
  FiHome,
  FiCheckSquare,
  FiEye,
} from "react-icons/fi";

import CitySelect from "@/components/shared/forms/CitySelect";
import AiItineraryUpload from "./AiItineraryUpload";
import type { TourPackageFormValues } from "@/pages/tour-packages/hooks/useTourPackageCreateForm";
import type { MealPlan } from "@/api/features/tours";
import type { AiTourImportResult } from "@/api/features/tours/types";
import { useUploadImage } from "@/api/features/uploads";
import clsx from "clsx";
import { useCurrenciesList, usePublicCurrenciesList } from "@/api/features/currencies";
import { HOTEL_CATEGORIES, ROOM_TYPES } from "@/api/features/tours/dropdowns";

const ACCENT = "#61dafbaa";

// Tours Cost fields shown per Mode of tour. Group prices by occupancy; Private
// prices by group size (2..6 pax). CWB/CNB/Infant columns are shared.
const GROUP_PRICE_FIELDS = [
  { name: "priceDouble", label: "Twin/Double" },
  { name: "priceTriple", label: "Triple" },
  { name: "priceChildWb", label: "Child with Bed" },
  { name: "priceChildWob", label: "Child without Bed" },
  { name: "priceInfant", label: "Infant" },
] as const;

const PRIVATE_PRICE_FIELDS = [
  { name: "price2Pax", label: "2 Pax" },
  { name: "price3Pax", label: "3 Pax" },
  { name: "price4Pax", label: "4 Pax" },
  { name: "price5Pax", label: "5 Pax" },
  { name: "price6Pax", label: "6 Pax" },
  { name: "priceChildWb", label: "CWB" },
  { name: "priceChildWob", label: "CNB" },
  { name: "priceInfant", label: "Infant" },
] as const;

/* =========================================
 * Helper Types (UI options)
 * ======================================= */

export type TourTypeOption = {
  id: number;
  name: string;
};

export type SupplierOption = {
  id: number;
  name: string;
};

/* =========================================
 * Props
 * ======================================= */

export type CreateTourPackageFormProps = {
  form: UseFormReturn<TourPackageFormValues>;
  handleSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;

  citiesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "cities",
    "id"
  >;
  tourRatesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "tourRates",
    "id"
  >;
  itineraryDaysFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "itineraryDays",
    "id"
  >;
  inclusionsFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "inclusions",
    "id"
  >;
  exclusionsFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "exclusions",
    "id"
  >;
  cancellationPoliciesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "cancellationPolicies",
    "id"
  >;
  hotelsFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "hotels",
    "id"
  >;
  imagesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "images",
    "id"
  >;
  departuresFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "departures",
    "id"
  >;

  tourTypes: TourTypeOption[];
  suppliers: SupplierOption[];
  supplierLocked?: boolean;
  onCancel?: () => void;
};

/* =========================================
 * Validation error helpers
 * ======================================= */

/** Flatten a react-hook-form errors object into a list of { path, message }. */
function flattenFormErrors(
  obj: any,
  path: string[] = [],
  out: { path: string; message: string }[] = [],
): { path: string; message: string }[] {
  if (!obj || typeof obj !== "object") return out;

  // A react-hook-form error leaf is identified by having a `type` (and usually a
  // `ref`/`message`). Detect it by `type` so we still catch errors whose `message`
  // is empty — and fall back to a readable message in that case.
  const isLeaf =
    typeof obj.type === "string" ||
    (typeof obj.message === "string" && obj.message.length > 0);
  if (isLeaf) {
    const message =
      (typeof obj.message === "string" && obj.message) ||
      (obj.type === "required" ? "Required" : "Invalid value");
    out.push({ path: path.join("."), message });
    return out;
  }

  for (const key of Object.keys(obj)) {
    if (key === "ref" || key === "type" || key === "message" || key === "types")
      continue;
    // `root` is react-hook-form's reserved namespace for form/server-level errors
    // (e.g. root.server) — not a fixable field, so keep it out of the field list.
    if (path.length === 0 && key === "root") continue;
    flattenFormErrors(obj[key], [...path, key], out);
  }
  return out;
}

/** Turn a dotted error path (e.g. "tour.currencyCode") into a readable field label. */
// Friendly labels for the known fields. A tour has a single rate, so the rate
// fields are shown by their own name (no "Rate 1/2/3" prefix).
const FIELD_LABELS: Record<string, string> = {
  "tour.name": "Tour name",
  "tour.tourTypeId": "Tour type",
  "tour.supplierId": "Supplier",
  "tour.nights": "Nights",
  "tour.days": "Days",
  "tour.currencyCode": "Currency",
  "tourRates.0.travelFrom": "Travel from",
  "tourRates.0.travelTo": "Travel to",
  "tourRates.0.bookFrom": "Book from",
  "tourRates.0.bookTo": "Book to",
  "tourRates.0.priceDouble": "Twin/Double price",
  "tourRates.0.priceTriple": "Triple price",
  "tourRates.0.priceChildWb": "Child with Bed price",
  "tourRates.0.priceChildWob": "Child without Bed price",
  "tourRates.0.priceInfant": "Infant price",
  "tourRates.0.price2Pax": "2 Pax price",
  "tourRates.0.price3Pax": "3 Pax price",
  "tourRates.0.price4Pax": "4 Pax price",
  "tourRates.0.price5Pax": "5 Pax price",
  "tourRates.0.price6Pax": "6 Pax price",
};

function prettyFieldLabel(path: string): string {
  if (FIELD_LABELS[path]) return FIELD_LABELS[path];
  return path
    .replace(/^tour\./, "")
    // Single rate per tour — drop the rate index instead of showing "Rate N".
    .replace(/^tourRates\.\d+\./, "")
    .replace(/^cities\.(\d+)\./, (_m, i) => `City ${Number(i) + 1} — `)
    .replace(/^itineraryDays\.(\d+)\./, (_m, i) => `Day ${Number(i) + 1} — `)
    .replace(/\.(\d+)\./g, (_m, i) => ` ${Number(i) + 1} — `)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/* =========================================
 * Main Component
 * ======================================= */

export function CreateTourPackageForm(props: CreateTourPackageFormProps) {
  const MAX_IMAGE_UPLOAD_BATCH = 5;

  const {
    form,
    handleSubmit,
    isSubmitting,
    tourRatesFieldArray,
    itineraryDaysFieldArray,
    inclusionsFieldArray,
    exclusionsFieldArray,
    cancellationPoliciesFieldArray,
    hotelsFieldArray,
    imagesFieldArray,
    departuresFieldArray,
    tourTypes,
    suppliers,
    supplierLocked = false,
    onCancel,
  } = props;

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors, submitCount },
  } = form;
  const hasValidationErrors =
    submitCount > 0 && Object.keys(errors ?? {}).length > 0;

  // After a failed save attempt, scroll the first invalid field into view and focus it,
  // so the user can immediately see what needs fixing.
  useEffect(() => {
    if (!submitCount) return;
    const list = flattenFormErrors(errors);
    if (!list.length) return;
    const firstPath = list[0].path;
    const el = document.querySelector(
      `[name="${firstPath}"]`,
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      try {
        el.focus({ preventScroll: true });
      } catch {
        /* not focusable — scrolling is enough */
      }
    }
    // Intentionally only react to submit attempts, not to every error change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  // Outline EVERY invalid field in red (and clear it once fixed) after a save attempt.
  // We drive this off the form `name` attributes so it covers all registered fields
  // without having to wire an error prop into each one individually.
  useEffect(() => {
    const clearAll = () => {
      document.querySelectorAll("[data-invalid-field='1']").forEach((node) => {
        const el = node as HTMLElement;
        el.style.borderColor = "";
        el.style.boxShadow = "";
        el.removeAttribute("data-invalid-field");
      });
    };

    if (!submitCount) {
      clearAll();
      return;
    }

    const invalidNames = new Set(flattenFormErrors(errors).map((e) => e.path));

    // Apply red outline to currently-invalid fields.
    invalidNames.forEach((name) => {
      document.querySelectorAll(`[name="${name}"]`).forEach((node) => {
        const el = node as HTMLElement;
        el.style.borderColor = "#f43f5e"; // rose-500
        el.style.boxShadow = "0 0 0 1px #fda4af"; // rose-300 ring
        el.setAttribute("data-invalid-field", "1");
      });
    });

    // Remove the outline from fields that are no longer invalid.
    document.querySelectorAll("[data-invalid-field='1']").forEach((node) => {
      const el = node as HTMLElement;
      const name = el.getAttribute("name") ?? "";
      if (!invalidNames.has(name)) {
        el.style.borderColor = "";
        el.style.boxShadow = "";
        el.removeAttribute("data-invalid-field");
      }
    });
  }, [errors, submitCount]);

  const tour = watch("tour");
  const selectedSupplierId = Number(watch("tour.supplierId") ?? 0);
  const selectedSupplierName =
    suppliers.find((s) => s.id === selectedSupplierId)?.name ??
    suppliers[0]?.name ??
    "Assigned Supplier";
  const itineraryDays = watch("itineraryDays") || [];
  const images = watch("images") || [];
  const hotels = watch("hotels") || [];
  const inclusions = watch("inclusions") || [];
  const exclusions = watch("exclusions") || [];
  const cancellationPolicies = watch("cancellationPolicies") || [];

  const hasFixedDepartures = watch("tour.hasFixedDepartures");
  const tourMode = watch("tour.tourMode");
  // const departures = watch("departures") || [];

  // Single-rate model: guarantee row 0 exists.
  useEffect(() => {
    if ((tourRatesFieldArray.fields?.length ?? 0) === 0) {
      tourRatesFieldArray.append({
        rateType: "BASE",
        status: "ACTIVE",
        priority: 0 as any,
        travelFrom: "",
        travelTo: "",
        bookFrom: "",
        bookTo: "",
        currencyCode: (form.getValues("tour.currencyCode") || "").toUpperCase(),
        priceDouble: "" as any,
        priceTriple: "" as any,
        priceChildWb: "" as any,
        priceChildWob: "" as any,
        priceInfant: "" as any,
        price2Pax: "" as any,
        price3Pax: "" as any,
        price4Pax: "" as any,
        price5Pax: "" as any,
        price6Pax: "" as any,
        isTaxInclusive: true,
        label: "",
        notes: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRatesFieldArray.fields?.length]);

  // Mode of tour is the single driver:
  //  - GROUP   ⇒ fixed departures (calendar shown).
  //  - PRIVATE ⇒ private flow (travel window, no fixed departures).
  const applyTourMode = (mode: "GROUP" | "PRIVATE") => {
    const isGroup = mode === "GROUP";

    setValue("tour.tourMode", mode, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("tour.hasFixedDepartures", isGroup, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("tour.isPrivateTour", !isGroup, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // For drag-reorder
  const [dragImageIndex, setDragImageIndex] =
    React.useState<number | null>(null);

  // For edit modal
  const [imageEditorIndex, setImageEditorIndex] =
    React.useState<number | null>(null);

  const activeImage =
    imageEditorIndex !== null && images[imageEditorIndex]
      ? images[imageEditorIndex]
      : null;

  const closeImageEditor = () => setImageEditorIndex(null);
  const uploadTourImageMutation = useUploadImage();

  // const nightsValue = watch("tour.nights");

  // const hasHydratedDeparturesRef = React.useRef(false);

  const didInitDeparturesRef = React.useRef(false);
const prevHasFixedDeparturesRef = React.useRef<boolean | undefined>(undefined);

useEffect(() => {
  if (!departuresFieldArray?.replace || !departuresFieldArray?.append) return;

  const prev = prevHasFixedDeparturesRef.current;
  prevHasFixedDeparturesRef.current = hasFixedDepartures;

  const len = departuresFieldArray.fields?.length ?? 0;

  // console.log("[FORM] departures effect", {
  //   prev,
  //   hasFixedDepartures,
  //   len,
  //   currentValues: form.getValues("departures"),
  // });

  // ✅ Only clear when user toggles ON -> OFF (prev true, now false)
  if (prev === true && hasFixedDepartures === false) {
    // console.log("[FORM] toggle OFF -> clearing departures");
    departuresFieldArray.replace([]);
    didInitDeparturesRef.current = false;
    return;
  }

  // ✅ When toggled OFF initially (prev undefined/false), do NOTHING (don’t wipe hydration)
  if (!hasFixedDepartures) return;

  // ✅ When toggled ON -> if empty, init once
  if (len === 0 && !didInitDeparturesRef.current) {
    didInitDeparturesRef.current = true;
    // console.log("[FORM] toggle ON -> init first departure row");

    departuresFieldArray.append({
      departureDate: "",
      capacity: "" as any,
      status: "ACTIVE",
    });
  }
}, [hasFixedDepartures, departuresFieldArray.fields?.length]);

  // For image preview modal
  // const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [imageUploadError, setImageUploadError] =
    React.useState<string | null>(null);
  const [imageUploadTotal, setImageUploadTotal] = React.useState(0);
  const [imageUploadCompleted, setImageUploadCompleted] = React.useState(0);
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);

  const normalizeImageSortOrder = React.useCallback(
    (list: TourPackageFormValues["images"]) =>
      (list ?? []).map((img, index) => ({
        ...img,
        sortOrder: index + 1,
      })),
    []
  );

  const replaceImagesWithNormalizedSortOrder = React.useCallback(
    (list: TourPackageFormValues["images"]) => {
      imagesFieldArray.replace(normalizeImageSortOrder(list));
    },
    [imagesFieldArray, normalizeImageSortOrder]
  );

  // Handle drag-and-drop image files (temporary blob URLs)
  const handleImageFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (isUploadingImage) return; // avoid parallel uploads

    setImageUploadError(null);

    const pickedFiles = Array.from(files);
    const filesToUpload = pickedFiles.slice(0, MAX_IMAGE_UPLOAD_BATCH);

    if (pickedFiles.length > MAX_IMAGE_UPLOAD_BATCH) {
      setImageUploadError(
        `You can upload up to ${MAX_IMAGE_UPLOAD_BATCH} images at a time. Only the first ${MAX_IMAGE_UPLOAD_BATCH} were selected.`
      );
    }

    setImageUploadTotal(filesToUpload.length);
    setImageUploadCompleted(0);
    setIsUploadingImage(true);

    try {
      const failures: string[] = [];
      const uploadResults = await Promise.all(
        filesToUpload.map(async (file) => {
          try {
            const result = await uploadTourImageMutation.mutateAsync({
              file,
              module: "tours",
              tag: "gallery",
            });
            return result?.url ?? null;
          } catch (err: any) {
            const raw =
              err?.response?.data?.message ||
              err?.message ||
              "Failed to upload image.";

            if (String(raw).toLowerCase().includes("file too large")) {
              failures.push("One or more images are too large. Please upload smaller files.");
            } else {
              failures.push(String(raw));
            }
            return null;
          } finally {
            setImageUploadCompleted((prev) => prev + 1);
          }
        })
      );

      const successfulUrls = uploadResults.filter(
        (url): url is string => Boolean(url)
      );

      if (successfulUrls.length > 0) {
        const currentImages = (form.getValues("images") ?? []) as TourPackageFormValues["images"];
        const nextImages = [
          ...currentImages,
          ...successfulUrls.map((url) => ({
            imageUrl: url,
            caption: "",
            sortOrder: "" as any,
          })),
        ];
        replaceImagesWithNormalizedSortOrder(nextImages);
      }

      if (failures.length > 0) {
        setImageUploadError(failures[0]);
      }
    } finally {
      setIsUploadingImage(false);
    }
  };


  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);

  // const mainHeroImage = images.find((img) => img.imageUrl)?.imageUrl || "";
  // Hero carousel images (from gallery)
  const heroImages = (images || []).filter((img) => img.imageUrl);

  // Current slide index for preview hero
  const [heroIndex, setHeroIndex] = useState(0);

  const showPrevHero = () => {
    if (!heroImages.length) return;
    setHeroIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const showNextHero = () => {
    if (!heroImages.length) return;
    setHeroIndex((prev) => (prev + 1) % heroImages.length);
  };

  // Admin users load currencies from admin list; suppliers use public lookup.
  const { data: currenciesPage, isLoading: adminCurrenciesLoading } = useCurrenciesList(
    {
      page: 1,
      limit: 100,
      q: undefined,
    },
    { enabled: !supplierLocked }
  );
  const { data: publicCurrencies, isLoading: publicCurrenciesLoading } = usePublicCurrenciesList(
    {
      limit: 100,
    },
    { enabled: supplierLocked }
  );

  const currenciesLoading = supplierLocked ? publicCurrenciesLoading : adminCurrenciesLoading;
  const currencyOptions = supplierLocked
    ? (publicCurrencies ?? [])
    : (currenciesPage?.items ?? []);

  // Calendar picker state (MUST be top-level hooks)
  const [departureCalendarMonth, setDepartureCalendarMonth] = React.useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [departureCalendarSelected, setDepartureCalendarSelected] = React.useState<Set<string>>(
      () => new Set()
  );

  const pad2 = (n: number) => String(n).padStart(2, "0");

  const toISODate = (d: Date) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

  const addMonths = (d: Date, delta: number) => new Date(d.getFullYear(), d.getMonth() + delta, 1);

  const startOfCalendar = (monthStart: Date) => {
    // Sunday-start calendar
    const day = monthStart.getDay(); // 0..6
    const res = new Date(monthStart);
    res.setDate(monthStart.getDate() - day);
    res.setHours(0, 0, 0, 0);
    return res;
  };

  const buildCalendarGrid = (monthStart: Date) => {
    const start = startOfCalendar(monthStart);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  };


  const monthStart = startOfMonth(departureCalendarMonth);
  const calendarGrid = buildCalendarGrid(monthStart);


  const existingDepartureDates = React.useMemo(() => {
  return new Set(
  (departuresFieldArray?.fields ?? [])
  .map((x: any) => (x?.departureDate ? String(x.departureDate).slice(0, 10) : ""))
  .filter(Boolean)
  );
  }, [departuresFieldArray?.fields]);


  const monthLabel = monthStart.toLocaleString(undefined, { month: "long", year: "numeric" });


  const toggleDepartureDate = (iso: string) => {
  setDepartureCalendarSelected((prev) => {
  const next = new Set(prev);
  if (next.has(iso)) next.delete(iso);
  else next.add(iso);
  return next;
  });
  };


  const saveSelectedDepartureDates = () => {
  const picks = Array.from(departureCalendarSelected).sort();
  if (picks.length === 0) return;


  picks.forEach((iso) => {
  if (existingDepartureDates.has(iso)) return;
  departuresFieldArray?.append?.({
  departureDate: iso,
  capacity: null as any,
  status: "ACTIVE",
  });
  });


  setDepartureCalendarSelected(new Set());
  };

  /**
   * Merge AI-extracted itinerary data into the form. Database references
   * (tour type, supplier) and images are preserved — the admin sets those.
   */
  const applyAiImport = (data: AiTourImportResult) => {
    const cur = form.getValues();
    const num = (n: number | null): number | "" => (n == null ? "" : n);
    const mealPlan = (m: string): MealPlan => {
      const up = (m || "").toUpperCase();
      return up === "HB" || up === "FB" ? (up as MealPlan) : "BB";
    };
    const rate = (r: string): "BASE" | "PROMO" =>
      (r || "").toUpperCase() === "PROMO" ? "PROMO" : "BASE";
    const currency = data.tour.currencyCode || cur.tour.currencyCode || "USD";
    const matchedTourTypeId =
      tourTypes.find(
        (t) =>
          t.name.trim().toLowerCase() ===
          (data.tour.tourTypeName || "").trim().toLowerCase()
      )?.id ?? cur.tour.tourTypeId;

    form.reset({
      ...cur,
      tour: {
        ...cur.tour,
        tourTypeId: matchedTourTypeId,
        name: data.tour.name || cur.tour.name,
        nights: num(data.tour.nights),
        days: num(data.tour.days),
        isPrivateTour: data.tour.isPrivateTour ?? cur.tour.isPrivateTour,
        tourMode: data.tour.isPrivateTour ? "PRIVATE" : cur.tour.tourMode,
        isMultiCity: data.tour.isMultiCity ?? cur.tour.isMultiCity,
        currencyCode: currency,
      },
      tourRates: data.tourRates.length
        ? data.tourRates.map((r, i) => ({
            rateType: rate(r.rateType),
            status: "ACTIVE" as const,
            priority: i,
            travelFrom: r.travelFrom || "",
            travelTo: r.travelTo || "",
            bookFrom: r.bookFrom || "",
            bookTo: r.bookTo || "",
            currencyCode: currency,
            priceDouble: num(r.priceDouble),
            priceTriple: num(r.priceTriple),
            priceChildWb: num(r.priceChildWb),
            priceChildWob: num(r.priceChildWob),
            priceInfant: num(r.priceInfant),
            // AI import covers group occupancy pricing only; pax tiers stay blank.
            price2Pax: "" as any,
            price3Pax: "" as any,
            price4Pax: "" as any,
            price5Pax: "" as any,
            price6Pax: "" as any,
            isTaxInclusive: true, // tours are always quoted tax-inclusive
            label: r.label || "",
            notes: r.notes || "",
          }))
        : cur.tourRates,
      cities: data.cities.length
        ? data.cities.map((c) => ({
            countryCode: c.countryCode || "",
            country: c.country || "",
            state: c.state || "",
            city: c.city || "",
            nightsInCity: num(c.nightsInCity),
          }))
        : cur.cities,
      itineraryDays: data.itineraryDays.length
        ? data.itineraryDays.map((d, i) => ({
            dayNumber: d.dayNumber ?? i + 1,
            title: d.title || "",
            description: d.description || "",
          }))
        : cur.itineraryDays,
      inclusions: data.inclusions.length
        ? data.inclusions.map((x, i) => ({
            sortOrder: i + 1,
            label: x.label || "",
            description: x.description || "",
          }))
        : cur.inclusions,
      exclusions: data.exclusions.length
        ? data.exclusions.map((x, i) => ({
            sortOrder: i + 1,
            label: x.label || "",
            description: x.description || "",
          }))
        : cur.exclusions,
      cancellationPolicies: data.cancellationPolicies.length
        ? data.cancellationPolicies.map((x) => ({
            label: x.label || "",
            description: x.description || "",
          }))
        : cur.cancellationPolicies,
      hotels: data.hotels.length
        ? data.hotels.map((h) => ({
            hotelName: h.hotelName || "",
            hotelCategory: h.hotelCategory || "",
            addressLine1: h.addressLine1 || "",
            addressLine2: h.addressLine2 || "",
            city: h.city || "",
            state: h.state || "",
            country: h.country || "",
            countryCode: h.countryCode || "",
            postalCode: h.postalCode || "",
            roomType: h.roomType || "",
            mealPlan: mealPlan(h.mealPlan),
          }))
        : cur.hotels,
      departures: data.departures.length
        ? data.departures.map((d) => ({
            departureDate: d.departureDate || "",
            capacity: num(d.capacity),
            status: "ACTIVE",
          }))
        : cur.departures,
      images: cur.images, // images are added manually
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 pb-24">
      {hasValidationErrors &&
        (() => {
          const list = flattenFormErrors(errors);
          const serverMsg = (errors as any)?.root?.server?.message as
            | string
            | undefined;
          return (
            <div className="mx-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <div className="font-medium">
                {serverMsg && list.length === 0
                  ? serverMsg
                  : "Please fix the following before saving:"}
              </div>
              {list.length > 0 && (
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                  {list.slice(0, 12).map((e, i) => (
                    <li key={`${e.path}-${i}`}>
                      <span className="font-medium">
                        {prettyFieldLabel(e.path)}
                      </span>
                      {e.message ? `: ${e.message}` : ""}
                    </li>
                  ))}
                  {list.length > 12 && <li>…and {list.length - 12} more</li>}
                </ul>
              )}
              {serverMsg && list.length > 0 && (
                <div className="mt-1 text-xs">{serverMsg}</div>
              )}
            </div>
          );
        })()}
      
      {/* Action bar — sticky below the shell header, with a small gap so it doesn't glue to it */}
      <div className="sticky top-[69px] z-10 -mx-4 border-b border-slate-200 bg-white px-4 py-2.5 shadow-sm sm:-mx-6 sm:px-6">
        <div className="flex items-center justify-end gap-2 p-px">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <FiEye className="h-3 w-3" />
            Preview
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? "Saving…" : "Save tour package"}
          </button>
        </div>
      </div>

      {/* AI-assisted prefill from an uploaded itinerary */}
      <div className="px-4">
        <AiItineraryUpload
          onImported={applyAiImport}
          disabled={isSubmitting}
          options={{
            tourTypes: tourTypes.map((t) => t.name),
            hotelCategories: [...HOTEL_CATEGORIES],
            roomTypes: [...ROOM_TYPES],
          }}
        />
      </div>

      <div className="flex flex-col gap-6 px-4 md:flex-row">
        {/* Left column */}
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          {/* Core tour info */}
          <SectionCard
            icon={<FiInfo />}
            title="Tour details"
            description="Basic info about this package."
          >
            {/* Row 1: Tour name + Tour type + Nights + Days */}
            <div className="grid gap-4 md:grid-cols-12">
              <div className="md:col-span-4">
                <Field
                  label="Tour name"
                  required
                  error={errors.tour?.name?.message as string | undefined}
                >
                  <input
                    {...register("tour.name", {
                      required: "Tour name is required",
                    })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                    placeholder="Dubai Delight – 4N/5D"
                  />
                </Field>
              </div>

              <div className="md:col-span-4">
                <Field label="Tour type" required>
                  <select
                    {...register("tour.tourTypeId", {
                      required: "Tour type is required",
                      valueAsNumber: true,
                    })}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  >
                    <option value="">Select tour type</option>
                    {tourTypes.map((tt) => (
                      <option key={tt.id} value={tt.id}>
                        {tt.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="md:col-span-2">
                <Field
                  label="Nights"
                  required
                  error={errors.tour?.nights?.message as string | undefined}
                >
                  <input
                    type="number"
                    min={1}
                    {...register("tour.nights", {
                      required: "Nights are required",
                      valueAsNumber: true,
                    })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  />
                </Field>
              </div>

              <div className="md:col-span-2">
                <Field
                  label="Days (auto)"
                  required
                  error={errors.tour?.days?.message as string | undefined}
                >
                  <input
                    type="number"
                    min={1}
                    readOnly
                    {...register("tour.days", {
                      required: "Days are required",
                      valueAsNumber: true,
                    })}
                    className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  />
                </Field>
              </div>
            </div>

            {/* Supplier + Status + Currency + Mode of tour — one line */}
            <div className="mt-4 grid gap-4 md:grid-cols-4">
              <div>
                <Field label="Supplier" required>
                  {supplierLocked ? (
                    <>
                      <input
                        type="hidden"
                        {...register("tour.supplierId", {
                          required: "Supplier is required",
                          valueAsNumber: true,
                        })}
                      />
                      <input
                        value={selectedSupplierName}
                        readOnly
                        className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none"
                      />
                    </>
                  ) : (
                    <select
                      {...register("tour.supplierId", {
                        required: "Supplier is required",
                        valueAsNumber: true,
                      })}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                    >
                      <option value="">Select supplier</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>

              <div>
                <Field label="Status" required>
                  <select
                    {...register("tour.status")}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </Field>
              </div>

              <div>
                <Field
                  label="Currency"
                  required
                  error={errors.tour?.currencyCode?.message as string | undefined}
                >
                  <Controller
                    control={control}
                    name="tour.currencyCode"
                    render={({ field: currencyField }) => (
                      <select
                        ref={currencyField.ref}
                        name="tour.currencyCode"
                        value={(currencyField.value ?? "").toString().toUpperCase()}
                        onChange={(e) => currencyField.onChange(e.target.value)}
                        onBlur={currencyField.onBlur}
                        disabled={currenciesLoading}
                        className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400 ${
                          errors.tour?.currencyCode
                            ? "border-rose-400 ring-1 ring-rose-300"
                            : "border-slate-300"
                        }`}
                      >
                        <option value="">
                          {currenciesLoading ? "Loading…" : "Select currency"}
                        </option>
                        {currencyOptions.map((c) => {
                          const code = String(c.code).toUpperCase();
                          return (
                            <option key={code} value={code}>
                              {code} — {c.name}
                            </option>
                          );
                        })}
                      </select>
                    )}
                  />
                </Field>
              </div>

              {/* Mode of tour — Group ⇒ fixed departures, Private ⇒ private flow */}
              <div>
                <Field label="Mode of tour" required>
                  <div className="flex w-full items-center rounded-md border border-slate-300 bg-white p-0.5 text-sm">
                    <button
                      type="button"
                      onClick={() => applyTourMode("GROUP")}
                      className={clsx(
                        "flex-1 rounded px-3 py-1.5 font-medium transition",
                        tourMode === "GROUP"
                          ? "bg-sky-500 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      Group
                    </button>
                    <button
                      type="button"
                      onClick={() => applyTourMode("PRIVATE")}
                      className={clsx(
                        "flex-1 rounded px-3 py-1.5 font-medium transition",
                        tourMode === "PRIVATE"
                          ? "bg-sky-500 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      Private
                    </button>
                  </div>
                </Field>
              </div>
            </div>

            {hasFixedDepartures && (
  <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3">
    <div className="mb-3 rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-medium text-slate-700">{monthLabel}</div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDepartureCalendarMonth((d) => addMonths(d, -1))}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            Prev
          </button>

          <button
            type="button"
            onClick={() => setDepartureCalendarMonth((d) => addMonths(d, 1))}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            Next
          </button>

          <button
            type="button"
            onClick={saveSelectedDepartureDates}
            disabled={departureCalendarSelected.size === 0}
            className="rounded-md border border-sky-300 bg-sky-50/40 px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-50"
          >
            Save selected ({departureCalendarSelected.size})
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-1 pb-1 text-[10px] font-medium text-slate-500">
            {d}
          </div>
        ))}

        {calendarGrid.map((d) => {
          const iso = toISODate(d);
          const inMonth = d.getMonth() === monthStart.getMonth();
          const isSelected = departureCalendarSelected.has(iso);
          const alreadyAdded = existingDepartureDates.has(iso);

          const base =
            "flex h-8 items-center justify-center rounded-md border text-xs select-none";
          const muted = !inMonth ? "opacity-40" : "";
          const added = alreadyAdded
            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";
          const selected = isSelected ? "ring-2 ring-sky-400" : "";

          return (
            <button
              key={iso}
              type="button"
              onClick={() => toggleDepartureDate(iso)}
              className={`${base} ${muted} ${added} ${selected}`}
              title={alreadyAdded ? "Already added" : "Click to select"}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-2 text-[11px] text-slate-500">
        Tip: Green dates are already added. Click dates to select/unselect, then
        hit Save selected.
      </div>
    </div>
  </div>
)}

          </SectionCard>

          {/* Images */}
          <SectionCard
            icon={<FiImage />}
            title="Images & gallery"
            description="Upload and arrange package images."
          >
            {/* Error banner for image upload */}
            {imageUploadError && (
              <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {imageUploadError}
              </div>
            )}

            {/* Top strip: count + thumbnails (bigger + draggable) */}
            <div className="mb-4 flex items-center justify-between gap-3">
              {/* Count */}
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <FiImage className="h-5 w-5 text-slate-500" />
                <span>{images.length} image(s) added</span>
                {isUploadingImage && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                    <span className="h-3 w-3 animate-spin rounded-full border border-slate-400 border-t-transparent" />
                    Uploading {imageUploadCompleted}/{imageUploadTotal}…
                  </span>
                )}
              </div>

              {/* Bigger thumbnails with drag + drop */}
              <div className="flex gap-4 overflow-x-auto pb-2">
                {images.map((img, index) =>
                  img.imageUrl ? (
                    <button
                      key={index}
                      type="button"
                      draggable
                      onDragStart={() => setDragImageIndex(index)}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (
                          dragImageIndex !== null &&
                          dragImageIndex !== index
                        ) {
                          const currentImages = (form.getValues("images") ??
                            []) as TourPackageFormValues["images"];
                          const nextImages = [...currentImages];
                          const [moved] = nextImages.splice(dragImageIndex, 1);
                          if (moved) {
                            nextImages.splice(index, 0, moved);
                            replaceImagesWithNormalizedSortOrder(nextImages);
                          }
                        }
                        setDragImageIndex(null);
                      }}
                      onClick={() => setImageEditorIndex(index)}
                      className="relative flex h-64 w-64 flex-shrink-0 items-center justify-center 
                                overflow-hidden rounded-xl border border-slate-200 bg-white 
                                shadow-sm transition hover:shadow-md"
                    >
                      <img
                        src={img.imageUrl}
                        alt={img.caption || `Image ${index + 1}`}
                        className="h-full w-full object-cover"
                      />

                      {/* Drag indicator dots */}
                      <div className="absolute bottom-1 left-1/2 flex -translate-x-1/2 gap-1 opacity-60">
                        <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        <div className="h-1.5 w-1.5 rounded-full bg-white" />
                      </div>
                    </button>
                  ) : null
                )}
              </div>
            </div>

            <div
              className={clsx(
                "mb-4 flex flex-col items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50/40 px-4 py-6 text-center text-xs text-slate-500",
                isUploadingImage && "cursor-not-allowed opacity-60"
              )}
              onDragOver={(e) => {
                if (isUploadingImage) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                if (isUploadingImage) return;
                e.preventDefault();
                e.stopPropagation();
                handleImageFiles(e.dataTransfer.files);
              }}
            >
              <FiImage className="mb-2 h-5 w-5 text-slate-400" />
              <p className="mb-1">
                {isUploadingImage
                  ? "Uploading images…"
                  : "Drag & drop images here, or click to browse."}
              </p>
              <p className="mb-3 text-[11px] text-slate-400">
                Images will be uploaded to your CDN. Max {MAX_IMAGE_UPLOAD_BATCH} files per upload.
              </p>
              <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                {isUploadingImage ? "Uploading…" : "Browse files"}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={isUploadingImage}
                  onChange={async (e) => {
                    await handleImageFiles(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
            </div>

            {/* Add empty slot (for later manual URL entry via editor) */}
            {/* <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() =>
                  imagesFieldArray.append({
                    imageUrl: "",
                    caption: "",
                    sortOrder: "" as any,
                  })
                }
                className="inline-flex w-fit items-center gap-2 rounded-md border border-dashed border-sky-300 bg-sky-50/40 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-50"
              >
                <FiImage className="h-3 w-3" />
                Add empty image slot
              </button>
            </div> */}
          </SectionCard>

          {/* Travel details — not needed for fixed departures (FD dates define travel) */}
          {!hasFixedDepartures && (
            <SectionCard
              icon={<FiMapPin />}
              title="Travel details"
              description="When guests can travel on this package."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label="From"
                  required
                  error={errors.tourRates?.[0]?.travelFrom?.message as string | undefined}
                >
                  <input
                    type="date"
                    {...register("tourRates.0.travelFrom" as const, {
                      required: "Travel start date is required",
                    })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  />
                </Field>
                <Field label="To">
                  <input
                    type="date"
                    {...register("tourRates.0.travelTo" as const)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  />
                </Field>
              </div>
            </SectionCard>
          )}

          {/* Booking details */}
          <SectionCard
            icon={<FiInfo />}
            title="Booking details"
            description="When this package can be booked."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="From">
                <input
                  type="date"
                  {...register("tourRates.0.bookFrom" as const)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                />
              </Field>
              <Field label="To">
                <input
                  type="date"
                  {...register("tourRates.0.bookTo" as const)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                />
              </Field>
            </div>
          </SectionCard>



          {/* Itinerary (Day-wise) */}
          <SectionCard
            icon={<FiList />}
            title="Itinerary"
            description="Day-wise overview of the tour."
          >
            <p className="mb-3 text-[11px] text-slate-500">
              Add a Title &amp; Description for each day. The number of days comes
              from the tour&apos;s total days.
            </p>

            <div className="flex flex-col gap-4">
              {itineraryDaysFieldArray.fields.length === 0 && (
                <div className="rounded-md border border-dashed border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-400">
                  Set the number of nights above to add itinerary days.
                </div>
              )}

              {itineraryDaysFieldArray.fields.map((dayField, dayIdx) => (
                <div
                  key={dayField.id}
                  className="border-b border-slate-100 pb-4 last:border-b-0"
                >
                  <div className="mb-2 text-xs font-semibold text-slate-700">
                    Day {dayIdx + 1}
                  </div>

                  <Field label="Title">
                    <input
                      {...register(`itineraryDays.${dayIdx}.title` as const)}
                      className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                      placeholder="Arrival, check-in"
                    />
                  </Field>

                  <div className="mt-2">
                    <Field label="Description">
                      <textarea
                        {...register(
                          `itineraryDays.${dayIdx}.description` as const
                        )}
                        rows={2}
                        className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                        placeholder="Airport pickup, hotel check-in, evening at leisure."
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Hotels */}
          <SectionCard
            icon={<FiHome />}
            title="Hotels"
            description="Hotels used in this package."
          >
            <div className="flex flex-col gap-3">
              {hotelsFieldArray.fields.map((field, index) => (
                <div
                  key={field.id}
                  className="border-b border-slate-100 pb-4 last:border-b-0"
                >
                  <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                    <span>Hotel {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => hotelsFieldArray.remove(index)}
                      className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700"
                    >
                      <FiX className="h-3 w-3" />
                      Remove
                    </button>
                  </div>

                  {/* City + Name + Category + Room type — one line.
                      City picker resolves country + state behind the scenes. */}
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[180px] flex-1">
                      <Controller
                        control={control}
                        name={`hotels.${index}` as const}
                        render={({ field: f }) => (
                          <CitySelect
                            value={{
                              countryCode: f.value?.countryCode,
                              country: f.value?.country,
                              state: f.value?.state,
                              city: f.value?.city,
                            }}
                            onChange={(next) =>
                              f.onChange({
                                ...f.value,
                                countryCode: next.countryCode ?? "",
                                country: next.country ?? "",
                                state: next.state ?? "",
                                city: next.city ?? "",
                              })
                            }
                            instancePrefix={`hotel-${index}`}
                          />
                        )}
                      />
                    </div>

                    <div className="min-w-[150px] flex-1">
                      <Field label="Hotel name">
                        <input
                          {...register(`hotels.${index}.hotelName` as const)}
                          className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                          placeholder="Hilton Dubai"
                        />
                      </Field>
                    </div>

                    <div className="min-w-[130px] flex-1">
                      <Field label="Category">
                        <select
                          {...register(
                            `hotels.${index}.hotelCategory` as const
                          )}
                          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                        >
                          <option value="">Select category</option>
                          {HOTEL_CATEGORIES.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    {/* Room type (free text) */}
                    <div className="min-w-[150px] flex-1">
                      <Field label="Room type">
                        <input
                          {...register(`hotels.${index}.roomType` as const)}
                          className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                          placeholder="Standard, Deluxe…"
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  hotelsFieldArray.append({
                    hotelName: "",
                    hotelCategory: "",
                    addressLine1: "",
                    addressLine2: "",
                    city: "",
                    state: "",
                    country: "",
                    countryCode: "",
                    postalCode: "",
                    roomType: "Standard",
                    mealPlan: "BB",
                  })
                }
                className="inline-flex w-fit items-center gap-2 rounded-md border border-dashed border-sky-300 bg-sky-50/40 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-50"
              >
                <FiHome className="h-3 w-3" />
                Add hotel
              </button>
            </div>
          </SectionCard>

          {/* Tours Cost — fields depend on Mode of tour (Group vs Private) */}
          <SectionCard
            icon={<FiDollarSign />}
            title="Tours Cost"
            description={
              tourMode === "PRIVATE"
                ? "Per-person pricing by group size (private tour)."
                : "Per-person pricing by occupancy (group tour)."
            }
          >
            <div
              className={clsx(
                "grid items-start gap-3",
                tourMode === "PRIVATE" ? "md:grid-cols-8" : "md:grid-cols-5"
              )}
            >
              {(tourMode === "PRIVATE"
                ? PRIVATE_PRICE_FIELDS
                : GROUP_PRICE_FIELDS
              ).map((f) => (
                <Field key={f.name} label={f.label} required>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    {...register(`tourRates.0.${f.name}` as const, {
                      valueAsNumber: true,
                    })}
                    className="w-full rounded-md border border-slate-300 px-2 py-2 text-xs outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  />
                </Field>
              ))}
            </div>

            <div className="mt-3">
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  {...register("tourRates.0.isTaxInclusive" as const)}
                  className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400"
                />
                Tax inclusive
              </label>
            </div>
          </SectionCard>



          {/* Inclusions / Exclusions*/}
          <SectionCard
            icon={<FiCheckSquare />}
            title="Inclusions & exclusions"
            description="What is and isn’t covered."
          >
            <div className="grid gap-4">
              {/* Inclusions */}
              <div className="flex flex-col gap-2">
                <div className="text-xs font-medium text-slate-700">
                  Inclusions
                </div>
                {inclusionsFieldArray.fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5"
                  >
                    <span className="text-[11px] text-slate-400">
                      {index + 1}.
                    </span>
                    <input
                      {...register(`inclusions.${index}.label` as const)}
                      className="flex-1 rounded-md border-none bg-transparent text-xs outline-none placeholder:text-slate-400"
                      placeholder="Daily breakfast"
                    />
                    <button
                      type="button"
                      onClick={() => inclusionsFieldArray.remove(index)}
                      className="text-slate-300 hover:text-slate-600"
                    >
                      <FiX className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    inclusionsFieldArray.append({
                      sortOrder: "" as any,
                      label: "",
                      description: "",
                    })
                  }
                  className="inline-flex w-fit items-center gap-2 rounded-md border border-dashed border-emerald-300 bg-emerald-50/40 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                >
                  Add inclusion
                </button>
              </div>

              {/* Exclusions */}
              <div className="flex flex-col gap-2">
                <div className="text-xs font-medium text-slate-700">
                  Exclusions
                </div>
                {exclusionsFieldArray.fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5"
                  >
                    <span className="text-[11px] text-slate-400">
                      {index + 1}.
                    </span>
                    <input
                      {...register(`exclusions.${index}.label` as const)}
                      className="flex-1 rounded-md border-none bg-transparent text-xs outline-none placeholder:text-slate-400"
                      placeholder="Visa not included"
                    />
                    <button
                      type="button"
                      onClick={() => exclusionsFieldArray.remove(index)}
                      className="text-slate-300 hover:text-slate-600"
                    >
                      <FiX className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    exclusionsFieldArray.append({
                      sortOrder: "" as any,
                      label: "",
                      description: "",
                    })
                  }
                  className="inline-flex w-fit items-center gap-2 rounded-md border border-dashed border-rose-300 bg-rose-50/40 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
                >
                  Add exclusion
                </button>
              </div>
            </div>
          </SectionCard>

          {/* Cancellation policies */}
          <SectionCard
            icon={<FiInfo />}
            title="Cancellation policy"
            description="Explain how cancellation and refunds work."
          >
            <div className="flex flex-col gap-3">
              {cancellationPoliciesFieldArray.fields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-md border border-slate-200 bg-white p-3"
                >
                  <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                    <span>Rule {index + 1}</span>
                    <button
                      type="button"
                      onClick={() =>
                        cancellationPoliciesFieldArray.remove(index)
                      }
                      className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700"
                    >
                      <FiX className="h-3 w-3" />
                      Remove
                    </button>
                  </div>

                  <div className="grid gap-3">
                    <Field label="Text">
                      <input
                        {...register(
                          `cancellationPolicies.${index}.label` as const
                        )}
                        className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                        placeholder="More than 30 days before departure – Full refund"
                      />
                    </Field>
                  </div>
                  <div className="mt-2">
                    <Field label="Description (optional)">
                      <textarea
                        {...register(
                          `cancellationPolicies.${index}.description` as const
                        )}
                        rows={2}
                        className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                      />
                    </Field>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  cancellationPoliciesFieldArray.append({
                    label: "",
                    description: "",
                  })
                }
                className="inline-flex w-fit items-center gap-2 rounded-md border border-dashed border-amber-300 bg-amber-50/40 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"
              >
                Add cancellation rule
              </button>
            </div>
          </SectionCard>

        </div>
      </div>

      {/* Image editor modal */}
      {imageEditorIndex !== null && activeImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
          <div className="relative flex w-full max-w-4xl flex-col gap-4 rounded-xl bg-white p-4 shadow-xl md:flex-row">
            {/* Close button */}
            <button
              type="button"
              onClick={closeImageEditor}
              className="absolute right-3 top-3 rounded-full bg-slate-100 p-1 text-slate-600 hover:bg-slate-200"
            >
              <FiX className="h-4 w-4" />
            </button>

            {/* Left: large preview / crop area placeholder */}
            <div className="flex flex-1 items-center justify-center rounded-lg bg-slate-900/90 p-3">
              {activeImage.imageUrl ? (
                <img
                  src={activeImage.imageUrl}
                  alt={activeImage.caption || "Preview"}
                  className="max-h-[70vh] max-w-full rounded-md object-contain"
                />
              ) : (
                <div className="text-xs text-slate-200">
                  No image URL yet. Upload or paste a URL on the right.
                </div>
              )}
            </div>

            {/* Right: URL + caption + order */}
            <div className="w-full max-w-sm space-y-4">
              <h3 className="text-sm font-semibold text-slate-900">
                Image details
              </h3>

              <Field label="Image URL">
                <input
                  {...register(
                    `images.${imageEditorIndex}.imageUrl` as const
                  )}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  placeholder="https://cdn.example/image.jpg"
                />
              </Field>

              <Field label="Caption">
                <input
                  {...register(
                    `images.${imageEditorIndex}.caption` as const
                  )}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs outline-none placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-sky-400"
                  placeholder="Short caption"
                />
              </Field>

              <Field label="Order">
                <input
                  type="number"
                  min={1}
                  {...register(
                    `images.${imageEditorIndex}.sortOrder` as const,
                    {
                      valueAsNumber: true,
                    }
                  )}
                  className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-transparent focus:ring-2 focus:ring-sky-400"
                />
              </Field>

              <div className="flex items-center justify-between gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    const currentImages = (form.getValues("images") ??
                      []) as TourPackageFormValues["images"];
                    const nextImages = currentImages.filter(
                      (_, idx) => idx !== imageEditorIndex
                    );
                    replaceImagesWithNormalizedSortOrder(nextImages);
                    closeImageEditor();
                  }}
                  className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
                >
                  Remove image
                </button>

                <button
                  type="button"
                  onClick={closeImageEditor}
                  className="rounded-md bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Itinerary-style preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/80 p-4">
          <div className="relative flex h-full max-h-[720px] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="absolute right-4 top-4 rounded-full bg-slate-100 p-1.5 text-slate-600 hover:bg-slate-200"
            >
              <FiX className="h-4 w-4" />
            </button>

            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Tour Itinerary
                </div>
                <h2 className="text-xl font-bold text-slate-900">
                  {tour?.name || "Untitled tour"}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                  {typeof tour?.nights === "number" &&
                    typeof tour?.days === "number" && (
                      <span className="inline-flex items-center rounded-full bg-sky-50 px-3 py-1 text-[11px] font-medium text-sky-700">
                        {tour.nights} Night{tour.nights === 1 ? "" : "s"} /{" "}
                        {tour.days} Day{tour.days === 1 ? "" : "s"}
                      </span>
                    )}
                </div>
              </div>
            </div>

            {/* Hero carousel */}
            {heroImages.length > 0 && (
              <div className="relative h-56 w-full overflow-hidden border-b border-slate-200 bg-slate-900">
                {/* Slide image */}
                <img
                  src={heroImages[heroIndex].imageUrl}
                  alt={heroImages[heroIndex].caption || "Tour hero"}
                  className="h-full w-full object-cover"
                />

                {/* Gradient overlay for text */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 to-transparent" />

                {/* Caption + counter */}
                <div className="absolute inset-x-0 bottom-2 flex items-end justify-between px-4 text-xs text-slate-50">
                  <div className="max-w-[70%] truncate">
                    {heroImages[heroIndex].caption && (
                      <span className="rounded-full bg-black/40 px-2 py-1 text-[11px]">
                        {heroImages[heroIndex].caption}
                      </span>
                    )}
                  </div>
                  <span className="rounded-full bg-black/40 px-2 py-1 text-[11px]">
                    {heroIndex + 1} / {heroImages.length}
                  </span>
                </div>

                {/* Left arrow */}
                {heroImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={showPrevHero}
                      className="absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-slate-50 backdrop-blur hover:bg-black/70"
                    >
                      <span className="text-lg leading-none">‹</span>
                    </button>

                    {/* Right arrow */}
                    <button
                      type="button"
                      onClick={showNextHero}
                      className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-slate-50 backdrop-blur hover:bg-black/70"
                    >
                      <span className="text-lg leading-none">›</span>
                    </button>

                    {/* Dots */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-1 flex justify-center gap-1">
                      {heroImages.map((_, idx) => (
                        <span
                          key={idx}
                          className={
                            "h-1.5 w-1.5 rounded-full " +
                            (idx === heroIndex ? "bg-white" : "bg-white/40")
                          }
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}


            {/* Body scroll area */}
            <div className="flex flex-1 flex-col overflow-auto px-6 py-4">
              {/* Top section: itinerary & hotels side by side */}
              <div className="grid gap-6 md:grid-cols-[2fr,1fr]">
                {/* Itinerary list */}
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-slate-900">
                    Day-wise Itinerary
                  </h3>
                  <div className="space-y-3">
                    {itineraryDays.length === 0 && (
                      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                        No itinerary days added yet.
                      </div>
                    )}

                    {itineraryDays.map((day, idx) => {
                      return (
                        <div
                          key={idx}
                          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3"
                        >
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-xs font-semibold text-white">
                                {idx + 1}
                              </span>
                              <div>
                                <div className="text-xs font-semibold text-slate-900">
                                  {day.title || `Day ${idx + 1}`}
                                </div>
                              </div>
                            </div>
                          </div>
                          {day.description && (
                            <p className="mt-1 text-xs leading-relaxed text-slate-700">
                              {day.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Hotels summary */}
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-slate-900">
                    Hotels
                  </h3>
                  <div className="space-y-3">
                    {hotels.length === 0 && (
                      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                        No hotel details added yet.
                      </div>
                    )}

                    {hotels.map((hotel, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-xs font-semibold text-slate-900">
                              {hotel.hotelName || `Hotel ${idx + 1}`}
                            </div>
                            <div className="mt-0.5 text-[11px] text-amber-700">
                              {hotel.hotelCategory || "Category not set"}
                            </div>
                            {(hotel.city || hotel.country) && (
                              <div className="mt-1 text-[11px] text-slate-500">
                                {[hotel.city, hotel.state, hotel.country]
                                  .filter(Boolean)
                                  .join(", ")}
                              </div>
                            )}
                            {hotel.roomType && (
                              <div className="mt-1 text-[11px] text-slate-500">
                                Room: {hotel.roomType}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom: inclusions, exclusions, cancellation */}
              <div className="mt-6 grid gap-6 md:grid-cols-3">
                {/* Inclusions */}
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-900">
                    Inclusions
                  </h3>
                  {inclusions.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No inclusions provided.
                    </p>
                  )}
                  <ul className="space-y-1 text-xs text-slate-700">
                    {inclusions.map((inc, idx) =>
                      inc.label ? (
                        <li key={idx} className="flex gap-2">
                          <span className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
                          <span>{inc.label}</span>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>

                {/* Exclusions */}
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-900">
                    Exclusions
                  </h3>
                  {exclusions.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No exclusions provided.
                    </p>
                  )}
                  <ul className="space-y-1 text-xs text-slate-700">
                    {exclusions.map((ex, idx) =>
                      ex.label ? (
                        <li key={idx} className="flex gap-2">
                          <span className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-rose-500" />
                          <span>{ex.label}</span>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>

                {/* Cancellation policy */}
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-900">
                    Cancellation policy
                  </h3>
                  {cancellationPolicies.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No cancellation policy provided.
                    </p>
                  )}
                  <ul className="space-y-2 text-xs text-slate-700">
                    {cancellationPolicies.map((cp, idx) =>
                      cp.label ? (
                        <li key={idx}>
                          <div className="font-medium">{cp.label}</div>
                          {cp.description && (
                            <div className="mt-0.5 text-[11px] text-slate-500">
                              {cp.description}
                            </div>
                          )}
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </form>
  );
}

/* =========================================
 * Small Presentational Components
 * ======================================= */

type SectionCardProps = {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
};

function SectionCard({ icon, title, description, children }: SectionCardProps) {
  // Flat, section-wise layout (no card chrome) — keep the icon + heading only.
  return (
    <section className="flex flex-col gap-4">
      <div
        className="flex items-center gap-2 border-b border-slate-200 pb-2"
        style={{ borderBottom: `2px solid ${ACCENT}` }}
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-sky-50 text-sky-600">
          {icon}
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          {description && (
            <div className="text-xs text-slate-500">{description}</div>
          )}
        </div>
      </div>
      <div>{children}</div>
    </section>
  );
}

type FieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
};

function Field({ label, required, error, children }: FieldProps) {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center gap-1 text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {error && (
        <span className="text-[11px] text-rose-600">{error}</span>
      )}
    </label>
  );
}
